import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { BookOpen, LogOut, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/core/auth/auth-context";
import { navPaths } from "@/core/constants";

const navItems = [
  // { to: navPaths.home, label: "Home", icon: House, end: true },
  {
    to: navPaths.knowledgeBases,
    label: "Knowledge Bases",
    icon: BookOpen,
    end: false,
  },
  {
    to: navPaths.infoSynthesis,
    label: "Information Synthesis",
    icon: MessageSquare,
    end: false,
  },
] as const;

export const AppSidebar = () => {
  const [expanded, setExpanded] = useState(false);
  const { session, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate(navPaths.login, { replace: true });
  };

  if (!session) return null;

  return (
    <aside
      className={cn(
        "sticky top-0 flex h-screen shrink-0 flex-col self-start border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out",
        expanded ? "w-60" : "w-16",
      )}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <div
        className={cn(
          "flex shrink-0 items-center border-b border-sidebar-border py-4",
          expanded ? "justify-start px-4" : "justify-center px-0",
        )}
      >
        <img
          src="/greennode.svg"
          alt="Greennode"
          className={cn(
            "w-auto transition-[height] duration-200  border",
            expanded ? "h-10 rounded-md" : "h-6 rounded-sm",
          )}
        />
        {expanded && (
          <span className="ml-5 text-xl font-bold text-[#2f8f36]">
            GreenRAG
          </span>
        )}
      </div>

      <nav className="flex flex-1 flex-col gap-1 px-2 py-4">
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            aria-label={expanded ? undefined : label}
            className={({ isActive }) =>
              cn(
                "flex items-center rounded-md py-2.5 text-sm font-medium transition-colors",
                expanded ? "gap-3 px-3" : "justify-center px-0",
                isActive
                  ? "bg-[#2f8f36] text-[#F8FAFC] hover:bg-[#247029]"
                  : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              )
            }
          >
            <Icon className="size-5 shrink-0" aria-hidden />
            {expanded ? <span className="truncate">{label}</span> : null}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto border-t border-sidebar-border p-2">
        {expanded ? (
          <p className="mb-2 truncate px-2 text-xs text-muted-foreground">
            {session.email}
          </p>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          className={cn(
            "w-full text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            expanded ? "justify-start gap-2 px-3" : "size-9 justify-center p-0",
          )}
          onClick={handleLogout}
          aria-label={expanded ? undefined : "Logout"}
        >
          <LogOut className="size-4 shrink-0" />
          {expanded ? <span>Logout</span> : null}
        </Button>
      </div>
    </aside>
  );
};
